import Immutable from 'immutable';
import moment from 'moment';

import Filter from 'sharedreact/data/Filter';
import FilterRule from 'sharedreact/data/FilterRule';
import FilterSupport from 'sharedreact/utils/FilterSupport';
import { compareValues } from 'sharedreact/utils/CompareSupport';

class FilterChecker {
  private acceptedKeywords: string[];
  private mapKeyWordToItemProperty: ((keyword: string) => string | null) | null;
  private _states: {name: string, value: string}[] | null;
  private getValue: ((item: any, attriute: string) => any) | null;

  constructor (
    acceptedKeywords: string[],
    keywordToItemPropertyMapper: ((keyword: string) => string | null) | null,
    states: {name: string, value: string}[] | null,
    getValue?: ((key: any, attribute: string) => any) | null
  ) {
    this.acceptedKeywords = acceptedKeywords;
    this.mapKeyWordToItemProperty = keywordToItemPropertyMapper;
    this._states = states;
    this.getValue = getValue ?? null;
  }

  private _itemValueConforms (itemValue: any, operator: string, filterValue: string): boolean {
    switch (operator) {
      case FilterRule.CONTAINS : {
        return itemValue && itemValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
      }
      case FilterRule.IS: {
        return compareValues(itemValue, filterValue) === 0;
      }
      case FilterRule.ISNOT: {
        return compareValues(itemValue, filterValue) !== 0;
      }
      case FilterRule.ISEMPTY: {
        return itemValue === null || itemValue === '';
      }
      case FilterRule.FROM: {
        return itemValue && itemValue.isSameOrAfter(moment(filterValue));
      }
      case FilterRule.UNTIL: {
        return itemValue && itemValue.isBefore(moment(filterValue));
      }
      case FilterRule.STARTS_WITH: {
        return itemValue && itemValue.toString().toLowerCase().startsWith(filterValue.toString().toLowerCase());
      }
      case FilterRule.ENDS_WITH: {
        return itemValue && itemValue.toString().toLowerCase().endsWith(filterValue.toString().toLowerCase());
      }
      case FilterRule.EXISTS: {
        return itemValue !== null;
      }
      default: {
        break;
      }
    }

    return false;
  }

  private _itemConformsToFilterRule (item: any, filterRule: FilterRule): boolean {
    if (filterRule.key == null) {
      for (const filterKeyword of this.acceptedKeywords) {
        const keyword: string | null = this.mapKeyWordToItemProperty != null ? this.mapKeyWordToItemProperty(filterKeyword) : filterKeyword;
        if (keyword) {
          const itemValue: any = (this.getValue ? this.getValue(item, keyword) : item.get(keyword));

          if (this._itemValueConforms(itemValue, filterRule.operator, filterRule.value ?? '')) {
            return true;
          }
        }
      }

      return false;
    } else {
      if (FilterRule.isSupportedKey(filterRule.key, this.acceptedKeywords)) {
        const itemKey: string | null = this.mapKeyWordToItemProperty != null ? this.mapKeyWordToItemProperty(filterRule.key) : filterRule.key;

        if (itemKey) {
          const itemValue: any = (this.getValue ? this.getValue(item, itemKey) : item.get(itemKey));

          return this._itemValueConforms(itemValue, filterRule.operator, filterRule.value ?? '');
        }
        // else unknown key, ignore
      }
      // else unknown key, ignore
    }

    return true;
  }

  itemConformsToFilter (item: any, filter: Filter, canExpandState?: boolean): boolean {
    const filterRules: Immutable.List<FilterRule> = FilterSupport.expand(filter, this.acceptedKeywords, this._states, canExpandState).rules;

    const ruleResults: boolean[] = [];

    for (const filterRule of filterRules) {
      if (FilterRule.isAND(filterRule)) {
        if (ruleResults.length > 1) {
          const lastElement: boolean = ruleResults.pop()!;
          const oneBeforeLastElement: boolean = ruleResults.pop()!;
          ruleResults.push(oneBeforeLastElement && lastElement);
        }
      } else if (FilterRule.isOR(filterRule)) {
        if (ruleResults.length > 1) {
          const lastElement: boolean = ruleResults.pop()!;
          const oneBeforeLastElement: boolean = ruleResults.pop()!;
          ruleResults.push(oneBeforeLastElement || lastElement);
        }
      } else {
        ruleResults.push(this._itemConformsToFilterRule(item, filterRule));
      }
    }

    if (ruleResults.length > 0) {
      return ruleResults.pop() ?? true;
    }

    return true;
  }

  itemConformsToFilters (item: any, filter: Filter, advancedFilter: Filter): boolean {
    return this.itemConformsToFilter(item, filter) && this.itemConformsToFilter(item, advancedFilter);
  }
}

export default FilterChecker;
